import React, { useEffect, useRef, useState } from "react";
import { FaMapMarkedAlt, FaMapMarkerAlt } from "react-icons/fa";
import InfoTip from "./InfoTip";
import {
  GOOGLE_MAPS_API_KEY,
  loadPlacesLibrary,
  mapsSearchUrl,
  parsePlace,
  searchOpenAddresses,
} from "../utils/googleMaps";
import "../assets/css/AddressSearch.css";

const AUTOFILL_INFO = "Pick a result to fill the address fields below.";

/**
 * Address search box. On selection calls onSelect(parsedAddress) — see parsePlace().
 * Uses Google Places when REACT_APP_GOOGLE_MAPS_API_KEY is set; otherwise (or if Google fails
 * to load) suggestions come from OpenStreetMap.
 */
function AddressSearch({ onSelect, viewParts = [], label = "Search address on Google" }) {
  const hostRef = useRef(null);
  const boxRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const [error, setError] = useState("");
  const [unavailable, setUnavailable] = useState(!GOOGLE_MAPS_API_KEY);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  onSelectRef.current = onSelect;

  useEffect(() => {
    if (!GOOGLE_MAPS_API_KEY) return undefined;
    let element = null;
    let cancelled = false;

    loadPlacesLibrary()
      .then(({ PlaceAutocompleteElement }) => {
        if (cancelled || !hostRef.current) return;
        element = new PlaceAutocompleteElement();
        element.setAttribute("placeholder", "Search business or address");
        element.addEventListener("gmp-select", async ({ placePrediction }) => {
          try {
            const place = placePrediction.toPlace();
            await place.fetchFields({
              fields: ["displayName", "formattedAddress", "addressComponents", "googleMapsURI", "types"],
            });
            onSelectRef.current?.(parsePlace(place));
          } catch (err) {
            console.error("Place details failed:", err);
            setError("Could not read that place. Try another result.");
          }
        });
        hostRef.current.appendChild(element);
      })
      .catch((err) => {
        console.error(err);
        if (!cancelled) setUnavailable(true);
      });

    return () => {
      cancelled = true;
      if (element) element.remove();
    };
  }, []);

  useEffect(() => {
    if (!unavailable) return undefined;
    const q = query.trim();
    if (q.length < 3) {
      setSuggestions([]);
      setSearching(false);
      return undefined;
    }
    const controller = new AbortController();
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const results = await searchOpenAddresses(q, { signal: controller.signal });
        setSuggestions(results);
        setActiveIndex(-1);
        setError("");
      } catch (err) {
        if (err.name !== "AbortError") {
          setSuggestions([]);
          setError("Address suggestions are unavailable right now. Enter the address below.");
        }
      } finally {
        if (!controller.signal.aborted) setSearching(false);
      }
    }, 350);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [query, unavailable]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const pickSuggestion = (item) => {
    setQuery(item.address.formattedAddress || item.title);
    setOpen(false);
    setSuggestions([]);
    onSelectRef.current?.(item.address);
  };

  const handleKeyDown = (e) => {
    if (!open || suggestions.length === 0) {
      if (e.key === "Enter") e.preventDefault();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      pickSuggestion(suggestions[activeIndex >= 0 ? activeIndex : 0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const viewUrl = mapsSearchUrl(viewParts) || mapsSearchUrl([query]);
  const mapButton = (
    <a
      className={`address-search__map${viewUrl ? "" : " is-disabled"}`}
      href={viewUrl || undefined}
      target="_blank"
      rel="noopener noreferrer"
      title={viewUrl ? "View on Google Maps" : "Enter an address to view it on Google Maps"}
      aria-label="View on Google Maps"
      onClick={(e) => {
        if (!viewUrl) e.preventDefault();
      }}
    >
      <FaMapMarkedAlt />
    </a>
  );

  const showList = unavailable && open && query.trim().length >= 3;

  return (
    <div className="form-group address-search">
      <label>
        <FaMapMarkerAlt className="address-search__icon" /> {label}
        <InfoTip text={AUTOFILL_INFO} label={`About ${label}`} />
      </label>
      {unavailable ? (
        <div className="address-search__fallback">
          <div className="address-search__combo" ref={boxRef}>
            <input
              type="text"
              value={query}
              placeholder="Search business or address"
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              onFocus={() => setOpen(true)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              role="combobox"
              aria-expanded={showList}
              aria-autocomplete="list"
            />
            {showList ? (
              <ul className="address-search__list" role="listbox">
                {searching && suggestions.length === 0 ? (
                  <li className="address-search__empty">Searching...</li>
                ) : suggestions.length === 0 ? (
                  <li className="address-search__empty">No matches. Try adding the area or city.</li>
                ) : (
                  suggestions.map((item, idx) => (
                    <li key={item.id} role="option" aria-selected={idx === activeIndex}>
                      <button
                        type="button"
                        className={`address-search__option${idx === activeIndex ? " is-active" : ""}`}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => pickSuggestion(item)}
                      >
                        <FaMapMarkerAlt className="address-search__option-icon" aria-hidden="true" />
                        <span className="address-search__option-text">
                          <span className="address-search__option-title">{item.title}</span>
                          {item.subtitle ? (
                            <span className="address-search__option-sub">{item.subtitle}</span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
          {mapButton}
        </div>
      ) : (
        <div className="address-search__fallback">
          <div ref={hostRef} className="address-search__host" />
          {mapButton}
        </div>
      )}
      {error ? <small className="form-hint address-search__error">{error}</small> : null}
    </div>
  );
}

export default AddressSearch;
